import { Injectable, Signal, computed, inject, signal } from '@angular/core';
import { TagsApi, type TagResponse } from '@tourfold/libraries/tf-api-client-public-v2';

/**
 * Thin wrapper over the generated `TagsApi` with an in-memory cache for the tag-editor
 * dropdown. The list is small (a few dozen rows per tenant in practice) and the staleness
 * window that matters is "did another user create a tag since I last looked?" — which only
 * matters when the user is actively about to pick one.
 *
 * Cache refresh policy:
 *  - `ensureLoaded()` lazy-loads once per session so initial chip rendering has names/colors.
 *  - `refresh()` is called by every `tf-tag-editor` instance whenever the user opens the
 *    "+ Add tag" affordance — that's the one moment freshness matters and a single GET is cheap.
 *  - `createTag()` writes the result to the cache optimistically, so the just-created chip is
 *    selectable immediately without a round-trip.
 */
@Injectable({ providedIn: 'root' })
export class TagsService {
  private readonly api = inject(TagsApi);
  private readonly _availableTags = signal<readonly TagResponse[]>([]);
  private loadStarted = false;

  readonly availableTags: Signal<readonly TagResponse[]> = computed(() => this._availableTags());

  /**
   * Lazy-load available tags exactly once per session. Subsequent calls are no-ops.
   * Failures are swallowed — the editor falls back to the empty list, which still allows
   * removal of already-attached tags but disables the "+ Add" path.
   */
  ensureLoaded(): void {
    if (this.loadStarted) return;
    this.loadStarted = true;
    void this.refresh();
  }

  async refresh(): Promise<void> {
    try {
      const response = await this.api.getTags({ size: 200 });
      this._availableTags.set(response.items ?? []);
    } catch {
      // Keep whatever we had.
    }
  }

  /**
   * Creates a new tag and prepends it to the cache so it's immediately selectable in any open
   * tag-editor. Returns the persisted tag for the caller to attach to whatever object it owns.
   */
  async createTag(name: string, color: string): Promise<TagResponse> {
    const created = await this.api.createTag({ createTagRequest: { name, color } });
    this._availableTags.update((current) => [created, ...current]);
    return created;
  }
}
